/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, DoCheck, OnInit, ViewChild} from '@angular/core';
import {PLAN_VALIDATION_STAGE, RULE_OUTCOME, TABS} from '../common/constants';
import {NLR_TABS as LeftComponentTabs} from '../common/constants';
import {PlanLodgementComponent} from './plan-lodgement/plan-lodgement.component';
import {GeneralCommentsComponent} from './general-comments/general-comments.component';
import {AgreementAndConsentComponent} from './agreement-and-consent/agreement-and-consent.component';
import {FormsModule, NgForm} from '@angular/forms';
import {PlanModelService} from '../common/service/plan-model.service';
import {PermissionsService} from '../common/service/permissions.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';

import {LandRegistryAdviceComponent} from './land-registry-advice/land-registry-advice.component';
import {FeeSummaryComponent} from './fee-summary/fee-summary.component';
import {FeeService} from '../common/service/fee.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {ActivatedRoute} from '@angular/router';
import {catchError, EMPTY, first, map, Observable, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FirmSettingsService} from '../common/service/firm-settings.service';
import {AuthenticatorService} from '../common/service/authenticator.service';
import {AttachmentsComponent} from './attachments/attachments.component';
import {SpatialUtilService} from '../design/services/spatial-util.service';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {switchMap} from 'rxjs/operators';
import {AlertModalService} from '../common/component/modal';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {RuleResultsResponse} from '../plan-detail/plan-detail.model';

@Component({
    selector: 'nlr-lodge-plan',
    imports: [
        PlanLodgementComponent,
        GeneralCommentsComponent,
        AgreementAndConsentComponent,
        FormsModule,
        LandRegistryAdviceComponent,
        FeeSummaryComponent,
        AttachmentsComponent
    ],
    templateUrl: './lodge-plan.component.html',
    styleUrl: './lodge-plan.component.scss'
})
export class LodgePlanComponent implements OnInit, DoCheck {
    @ViewChild('lodgePlanForm')
    lodgePlanForm: NgForm;

    protected readonly title = TABS.LODGE;

    savePermissions = ['EDIT_LODGE', 'VIEW_LODGE'];

    validatePermissions = ['EDIT_PLAN', 'VIEW_PLAN'];

    private routeAction: string = '';

    private lotSyncAtLastCheck: boolean;

    private spatialLotCountAtLastCheck: number = 0;

    constructor(
        private alertModalService: AlertModalService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private authenticatorService: AuthenticatorService,
        private feeService: FeeService,
        private firmSettingsService: FirmSettingsService,
        private notificationAlertsService: NotificationAlertsService,
        private permissionsService: PermissionsService,
        private planDetailsService: NlrPlanDetailService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private route: ActivatedRoute,
        private spatialUtilService: SpatialUtilService
    ) {}

    ngOnInit() {
        this.route.params
            .pipe(
                map(params => (this.routeAction = params['action'])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.eventBus
            .on(`nlr:tabChange:${LeftComponentTabs.LODGE}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isPlanEditable()) {
                    this.initLotSync();
                    this.initCustomers();
                    this.initLodgementEmailAddress();
                    this.calculateLots();
                }
                this.feeService.populateFees();
                this.updateAttachments();
            });

        this.eventBus
            .on(`updatePlanLots`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isPlanEditable()) {
                    this.calculateLots();
                }
                this.feeService.populateFees();
            });

        this.eventBus
            .on(`REPOPULATE_FEES`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isPlanEditable()) {
                    this.calculateLots();
                }
                this.feeService.populateFees();
            });

        this.eventBus
            .on('SAVE_LODGE')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.save());

        this.eventBus
            .on<boolean>('VALIDATE_LODGE')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => this.validate(meta.data));
    }

    ngDoCheck() {
        const model = this.getModel();
        if (!model.lotSync && this.lotSyncAtLastCheck !== model.lotSync) {
            model.applicationForTitlesDocumentIdentifier = null;
        }
        this.lotSyncAtLastCheck = model.lotSync;

        const spatialLotCount = this.spatialUtilService.getLots().length;
        if (spatialLotCount !== this.spatialLotCountAtLastCheck) {
            this.calculateLots();
        }
    }

    isSaveVisible() {
        return this.permissionsService.hasPermission(this.savePermissions);
    }

    isSaveDisabled() {
        return (
            this.planModelService.readonly ||
            !this.planLodgementService.hasMinimumPlanDetails() ||
            !this.permissionsService.hasEditPermission(this.savePermissions)
        );
    }

    save() {
        console.log(`${this.title} component - save`);

        if (this.isSaveDisabled()) {
            return;
        }

        const subscription = this.planLodgementService
            .save({showWarningForCertifiedPlan: false})
            .subscribe({complete: () => subscription.unsubscribe()});

        this.eventBus.cast('nlr:busy', subscription);
    }

    isValidateVisible() {
        return this.permissionsService.hasPermission(this.validatePermissions);
    }

    isValidateDisabled() {
        return (
            this.planModelService.readonly ||
            !this.planLodgementService.hasMinimumPlanDetails() ||
            !this.permissionsService.hasEditPermission(this.validatePermissions)
        );
    }

    validate(skipSave = false) {
        console.log(`${this.title} component - validate`);

        if (this.isValidateDisabled()) {
            return;
        }

        this.lodgePlanForm.onSubmit(undefined);

        let observable: Observable<any>;

        if (this.planModelService.readonly) {
            observable = this.planDetailsService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_LODGE);
        } else if (skipSave) {
            observable = this.planLodgementService.updateAttachments(true).pipe(
                switchMap(() => this.planDetailsService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_LODGE)),
                catchError(reason => {
                    this.alertModalService.createAlertMessage(reason);
                    return EMPTY;
                })
            );
        } else {
            this.planLodgementService.increaseValidationAttempt();
            observable = this.planModelService.save().pipe(
                switchMap(() => this.planLodgementService.updateAttachments(true)),
                switchMap(() => this.planDetailsService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_LODGE)),
                catchError(reason => {
                    this.alertModalService.createAlertMessage(reason);
                    return EMPTY;
                })
            );
        }

        const subscription = observable
            .pipe(
                first(),
                tap((response: RuleResultsResponse) => {
                    if (this.lodgePlanForm.invalid) {
                        this.notificationAlertsService.addMessage({
                            type: 'error',
                            text: 'The Lodge page is incomplete or data entered is invalid!',
                            subType: 'LodgeForm'
                        });
                    }

                    if (
                        response.ruleResults.some(
                            result =>
                                (result.type === RULE_OUTCOME.CRITICAL || result.type === RULE_OUTCOME.ERROR) &&
                                result.stage === PLAN_VALIDATION_STAGE.LODGEMENT_LODGE
                        )
                    ) {
                        this.notificationAlertsService.addMessage({
                            type: 'error',
                            text: 'The Lodge page has business rule errors!',
                            subType: 'LodgeRules'
                        });
                    }
                })
            )
            .subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    isCancelDisabled() {
        return this.planModelService.readonly;
    }

    cancel() {
        console.debug(`${this.title} component - cancel`);

        this.planLodgementService.cancel();
    }

    private initLotSync() {
        const model = this.getModel();
        if (model.planPurpose?.lotSync && typeof model.lotSync === 'undefined') {
            model.lotSync = false;
            model.applicationForTitlesDocumentIdentifier = null;
        }
    }

    private initCustomers() {
        this.planLodgementService.loadCustomers();
    }

    private initLodgementEmailAddress() {
        const model = this.getModel();
        if (model.lodgementEmailAddress) {
            return;
        }
        const subscription = this.firmSettingsService.getFirmEmailAddress().subscribe({
            next: firmEmailAddress => {
                console.debug(`${this.title} component - getFirmEmailAddress response:`, firmEmailAddress);
                model.lodgementEmailAddress = firmEmailAddress;
            },
            error: error => {
                console.error(`${this.title} component - getFirmEmailAddress error:`, error);
                model.lodgementEmailAddress = this.authenticatorService.getUsername();
            },
            complete: () => {
                console.debug(`${this.title} component - lodgement receipt email address set to ` + model.lodgementEmailAddress);
            }
        });

        this.eventBus.cast('nlr:busy', subscription);
    }

    private calculateLots() {
        this.planLodgementService.calculatePlanLots();
        this.feeService.calculateChargeablePlanLots();
    }

    private isPlanEditable() {
        return ['new', 'edit', 'amend', 'replace'].includes(this.routeAction);
    }

    private getModel() {
        return this.planModelService.getModel();
    }

    private updateAttachments() {
        const subscription = this.planLodgementService.updateAttachments(true).subscribe();
        this.eventBus.cast('nlr:busy', subscription);
    }
}
